"use client";

import Link from "next/link";
import {
  Authenticated,
  Unauthenticated,
  AuthLoading,
} from "convex/react";
import { useAuth } from "@workos-inc/authkit-nextjs/components";
import { LogOut, User } from "@/components/icons";
import { CartIconButton } from "@/components/cart/cart-icon-button";
import { normalizeAccountType } from "@/lib/auth/types";
import { NotificationBell } from "@/app/(protected)/dashboard/components/notification-bell";
import { resolveDashboardAudience } from "@/app/(protected)/dashboard/components/navigation/account-type-policy";

import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MobileNavigation } from "./mobile-navigation";
import { SignInLink } from "@/components/auth/sign-in-link";
import { accountMenuGroups } from "./account-menu-links";
import { useAccountContext } from "./use-account-context";

function AccountMenuTrigger({ pictureUrl }: { pictureUrl?: string | null }) {
  return (
    <DropdownMenuTrigger asChild>
      <Button
        variant="ghost"
        size="icon-compact"
        aria-label="Account menu"
      >
        <Avatar className="size-8">
          {pictureUrl && <AvatarImage src={pictureUrl} alt="" />}
          <AvatarFallback>
            <User className="size-4" aria-hidden />
          </AvatarFallback>
        </Avatar>
      </Button>
    </DropdownMenuTrigger>
  );
}

function AccountMenuLinks({ isAdmin }: { isAdmin: boolean }) {
  return accountMenuGroups(isAdmin).map((group) => (
    <div key={group[0]?.href}>
      <DropdownMenuSeparator />
      {group.map(({ href, icon: Icon, label }) => (
        <DropdownMenuItem key={href} asChild>
          <Link href={href} className="flex items-center gap-2">
            <Icon className="size-4" aria-hidden />
            {label}
          </Link>
        </DropdownMenuItem>
      ))}
    </div>
  ));
}

function AccountMenu() {
  const { user, signOut } = useAuth();
  const { isAdmin, company } = useAccountContext();

  if (!user) return null;
  const displayName =
    [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email;

  return (
    <DropdownMenu>
      <AccountMenuTrigger pictureUrl={user.profilePictureUrl} />
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel>
          <div className="flex flex-col gap-0.5">
            <span className="truncate text-sm font-semibold text-foreground">
              {displayName}
            </span>
            {company && (
              <span className="truncate text-xs font-normal text-muted-foreground">
                {company.name}
              </span>
            )}
          </div>
        </DropdownMenuLabel>
        <AccountMenuLinks isAdmin={isAdmin} />
        <DropdownMenuSeparator />
        <DropdownMenuItem
          variant="destructive"
          onClick={() => signOut({ returnTo: "/" })}
        >
          <LogOut className="size-4" aria-hidden />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function AccountActivityBell() {
  const { company } = useAccountContext();
  if (!company) return null;
  const audience = resolveDashboardAudience(
    company.accountTypes,
    normalizeAccountType(company.accountType),
  );
  return (
    <NotificationBell companyId={company._id} accountTypes={audience.accountTypes} />
  );
}

function GuestActions() {
  return (
    <>
      <CartIconButton />
      <Button variant="ghost" asChild size="default">
        <SignInLink href="/sign-in">Log in</SignInLink>
      </Button>
      <Button variant="primary" asChild size="default">
        <SignInLink href="/sign-up">Sign up</SignInLink>
      </Button>
    </>
  );
}

function SignedInActions() {
  return (
    <>
      <CartIconButton />
      <AccountActivityBell />
      <AccountMenu />
    </>
  );
}

export function NavbarShell() {
  return (
    <div className="flex items-center gap-3">
      <div className="hidden items-center gap-2 xl:flex">
        <AuthLoading>
          <div aria-hidden="true" className="invisible h-8 w-[200px]" />
        </AuthLoading>
        <Authenticated>
          <SignedInActions />
        </Authenticated>
        <Unauthenticated>
          <GuestActions />
        </Unauthenticated>
      </div>

      <div className="flex items-center gap-1 xl:hidden">
        <CartIconButton />
      </div>
      <MobileNavigation />
    </div>
  );
}
